/**
 * McpPage — Public page for the TourismIntel MCP Server.
 * Route: /mcp (English, public, SEO-indexed)
 *
 * The tool reference is rendered from GET /api/mcp/catalog (server = single source of truth);
 * components/mcp/mcpCatalogFallback.json is the static copy used when the request fails.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Bot, Copy, Check, KeyRound, Plug, Shield, Sparkles, Terminal, Zap, AlertTriangle } from "lucide-react";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import { ProBetaRequestForm } from "../components/ProBetaRequestForm";
import McpToolReference from "../components/mcp/McpToolReference";
import McpResponses from "../components/mcp/McpResponses";
import McpChatGptWaitlist from "../components/mcp/McpChatGptWaitlist";
import McpRoadmap from "../components/mcp/McpRoadmap";
import McpFaq, { MCP_FAQ } from "../components/mcp/McpFaq";
import McpManageKey from "../components/mcp/McpManageKey";
import { useMcpCatalog } from "../components/mcp/useMcpCatalog";
import { API } from "../config";
import { useAuth } from "../AuthContext";

const MCP_URL = "https://tourismintel.ai/api/mcp";

const StatusBadge = () => {
  const [status, setStatus] = useState("checking");
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/mcp/status`, { timeout: 8000 })
      .then(({ data }) => { if (alive) setStatus(data.status === "operational" ? "operational" : "degraded"); })
      .catch(() => { if (alive) setStatus("unavailable"); });
    return () => { alive = false; };
  }, []);
  const cfg = {
    checking: { dot: "#94a3b8", text: "Checking status…", color: "#94a3b8" },
    operational: { dot: "#34d399", text: "MCP Server operational", color: "#34d399" },
    degraded: { dot: "#fbbf24", text: "Degraded performance", color: "#fbbf24" },
    unavailable: { dot: "#f87171", text: "Status unavailable", color: "#f87171" },
  }[status];
  return (
    <div data-testid="mcp-status-badge" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 999, padding: "5px 14px", marginTop: 20 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.dot, boxShadow: status === "operational" ? `0 0 6px ${cfg.dot}` : "none", animation: status === "operational" ? "mcpPulse 2s ease-in-out infinite" : "none" }} />
      <span style={{ fontSize: 12, fontWeight: 600, color: cfg.color }}>{cfg.text}</span>
      <style>{`@keyframes mcpPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }`}</style>
    </div>
  );
};

const CodeBlock = ({ code, id }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };
  return (
    <div style={{ position: "relative", marginTop: 10 }}>
      <button
        onClick={copy}
        data-testid={`copy-${id}`}
        aria-label="Copy to clipboard"
        style={{
          position: "absolute", top: 8, right: 8, background: "#252e2f", border: "none",
          borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: copied ? "#34d399" : "#94a3b8",
          display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600,
        }}
      >
        {copied ? <Check style={{ width: 13, height: 13 }} /> : <Copy style={{ width: 13, height: 13 }} />}
        {copied ? "Copied" : "Copy"}
      </button>
      <pre
        data-testid={`code-${id}`}
        style={{
          margin: 0, background: "#0c1011", color: "#a5f3fc", borderRadius: 12, padding: "16px 18px",
          fontSize: 12, lineHeight: 1.6, overflowX: "auto", fontFamily: "ui-monospace, monospace",
        }}
      >{code}</pre>
    </div>
  );
};

const sectionH2 = { margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: "#191c1d", fontFamily: "'Reckless Neue Variable', serif" };
const bodyText = { fontSize: 15, color: "#3b494c", lineHeight: 1.7 };

export default function McpPage() {
  const { user, authHeader } = useAuth();
  const { catalog, live } = useMcpCatalog();
  const counts = catalog?.counts || { open: 10, pro: 11 };
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [issued, setIssued] = useState(null);   // { key, email_sent, ... }
  const [formError, setFormError] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);

  useEffect(() => {
    document.title = "TourismIntel MCP Server — news, briefs, reports, industry events & trends for AI agents";
    const desc = document.querySelector('meta[name="description"]');
    const original = desc?.getAttribute("content");
    desc?.setAttribute("content",
      "Connect Claude, Cursor or any MCP client to TourismIntel: 10 free read-only tools for curated travel-industry news, weekly Travel Briefs, report briefs, industry events (organiser-confirmed dates, .ics) and trend detection. ChatGPT support via OAuth coming soon.");
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "mcp-jsonld";
    ld.textContent = JSON.stringify([{
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "TourismIntel MCP Server",
      applicationCategory: "DeveloperApplication",
      operatingSystem: "Any",
      description: "Model Context Protocol server exposing TourismIntel's curated travel-industry intelligence — news, weekly Travel Briefs, reports & report briefs, industry events and trends — to AI assistants such as Claude and Cursor (10 Open tools, 1 Pro tool; ChatGPT support via OAuth coming soon).",
      url: "https://tourismintel.ai/mcp",
      featureList: "search_news, get_brief, list_briefs, list_reports, get_report_brief, list_events, get_trending, get_clusters, latest_newsletter, list_podcast_episodes, ask_tourismintel (Pro)",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD", description: "Free Open tier — 10 tools, 30 requests/min, 500/day" },
      publisher: { "@type": "Organization", name: "TourismIntel", url: "https://tourismintel.ai" },
    }, {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: MCP_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    }]);
    document.head.appendChild(ld);
    return () => {
      if (original) desc?.setAttribute("content", original);
      document.getElementById("mcp-jsonld")?.remove();
    };
  }, []);

  const requestKey = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/mcp/request-key`,
        { name: name.trim(), email: user ? undefined : email.trim(), accept_terms: acceptTerms },
        { headers: authHeader() }
      );
      setIssued(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setFormError(
        status === 429 ? "Too many requests from your network — try again in an hour."
          : typeof detail === "string" ? detail
            : "Something went wrong — please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyIssuedKey = async () => {
    try {
      await navigator.clipboard.writeText(issued.key);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch { /* noop */ }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", fontFamily: "'Space Grotesk', sans-serif" }}>
      <PublicPageNav active="mcp" />

      {/* Hero */}
      <section style={{ background: "#0c1011", padding: "64px 24px 56px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.25)", borderRadius: 999, padding: "5px 14px", marginBottom: 18 }}>
            <Sparkles style={{ width: 13, height: 13, color: "#22d3ee" }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#22d3ee" }}>New · Model Context Protocol</span>
          </div>
          <h1
            data-testid="mcp-page-title"
            style={{ margin: 0, fontSize: "clamp(30px, 4.5vw, 46px)", fontWeight: 700, letterSpacing: "-0.03em", color: "#f1f5f9", lineHeight: 1.1, fontFamily: "'Reckless Neue Variable', serif" }}
          >
            Travel-industry intelligence,<br />inside your AI assistant.
          </h1>
          <p data-testid="mcp-hero-copy" style={{ margin: "18px 0 0", fontSize: 17, color: "#94a3b8", lineHeight: 1.6, maxWidth: 680 }}>
            The <strong style={{ color: "#cbd5e1" }}>TourismIntel MCP Server</strong> gives Claude, Cursor and any MCP client
            six data domains in one connection: curated <strong style={{ color: "#cbd5e1" }}>news</strong>, the weekly{" "}
            <strong style={{ color: "#cbd5e1" }}>Travel Brief</strong>, <strong style={{ color: "#cbd5e1" }}>reports &amp; report briefs</strong>,{" "}
            the <strong style={{ color: "#cbd5e1" }}>industry events</strong> calendar, real-time <strong style={{ color: "#cbd5e1" }}>trends</strong> and{" "}
            <strong style={{ color: "#cbd5e1" }}>Pulse official statistics</strong> (Pro).
            Free key, one minute to set up — <span style={{ color: "#cbd5e1" }}>ChatGPT via OAuth coming soon</span>.
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 28, flexWrap: "wrap" }}>
            <button
              onClick={() => scrollTo("get-key")}
              data-testid="mcp-hero-get-key-btn"
              style={{ background: "#22d3ee", color: "#001f24", border: "none", borderRadius: 999, padding: "12px 26px", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
            >
              <KeyRound style={{ width: 16, height: 16 }} /> Get your free key
            </button>
            <button
              onClick={() => scrollTo("connect")}
              data-testid="mcp-hero-connect-btn"
              style={{ background: "transparent", color: "#cbd5e1", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 999, padding: "12px 26px", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
            >
              <Plug style={{ width: 16, height: 16 }} /> How to connect
            </button>
          </div>
          <div><StatusBadge /></div>
        </div>
      </section>

      {/* What is MCP */}
      <section style={{ padding: "48px 24px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <Bot style={{ width: 18, height: 18, color: "#006874" }} />
            <h2 style={sectionH2}>What is MCP?</h2>
          </div>
          <p style={bodyText}>
            The <a href="https://modelcontextprotocol.io" target="_blank" rel="noopener noreferrer" style={{ color: "#006874", fontWeight: 600 }}>Model Context Protocol</a> is
            the open standard that lets AI assistants use external tools and data sources. Once connected,
            your assistant can <em>call TourismIntel directly</em> — searching our news archive, pulling the
            latest Travel Brief, reading a report brief, listing upcoming industry events or checking what&apos;s trending — and use real, curated data in its answers
            instead of guessing.
          </p>
        </div>
      </section>

      {/* Use cases */}
      <section style={{ background: "#fff", padding: "48px 24px", borderTop: "1px solid #e8ecee", borderBottom: "1px solid #e8ecee" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <Zap style={{ width: 18, height: 18, color: "#006874" }} />
            <h2 style={sectionH2}>What you can do</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
            {[
              { t: "Morning industry scan", d: "\u201CWhat happened in hotel tech this week? Summarise the 5 most important stories.\u201D" },
              { t: "Trend detection", d: "\u201CWhich travel stories are going viral right now, and why do they matter for a DMO?\u201D" },
              { t: "Research assistant", d: "\u201CFind recent reports about AI adoption in hospitality and compare their forecasts.\u201D" },
              { t: "Brief-powered content", d: "\u201CDraft a LinkedIn post based on this week's TourismIntel Travel Brief.\u201D" },
              { t: "Competitive monitoring", d: "\u201CSearch the last 90 days of news about Booking.com's AI strategy.\u201D" },
              { t: "Event scouting", d: "\u201CWhich hospitality trade shows are in Europe in November? Dates, city, access type and .ics links.\u201D" },
              { t: "Event calendar for a DMO", d: "\u201CBuild me a Q1 2027 event calendar for a DMO: destinations and MICE events, with why each matters and the .ics links.\u201D" },
              { t: "Cited answers (PRO)", d: "\u201CIs airline NDC adoption accelerating? Answer with citations from the archive.\u201D" },
            ].map((u) => (
              <div key={u.t} style={{ background: "#f8f9fa", border: "1px solid #e8ecee", borderRadius: 14, padding: 18 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#191c1d" }}>{u.t}</p>
                <p style={{ margin: "8px 0 0", fontSize: 13, color: "#6b7a7d", lineHeight: 1.6, fontStyle: "italic" }}>{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section style={{ padding: "48px 24px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <Shield style={{ width: 18, height: 18, color: "#006874" }} />
            <h2 style={sectionH2}>Tiers &amp; limits</h2>
          </div>
          <div data-testid="mcp-tier-table" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
            {[
              {
                tier: "Open", price: "Free", accent: "#006874",
                items: [`${counts.open} read-only tools: news, briefs, reports + report briefs, industry events, trends, clusters, newsletter, podcast`, "30 requests / minute", "500 requests / day", "Self-service key — get it below"],
              },
              {
                tier: "Pro", price: "Beta by request", accent: "#7c3aed",
                items: [`Everything in Open (${counts.pro} tools)`, "Pulse data — official statistics (OECD, Eurostat, UN Tourism, ISTAT, Banca d'Italia): KPIs, series, rankings, compare", "pulse_ask — Ask the Data, cited answers on the numbers (15 / day)", "ask_tourismintel — RAG answers with citations from the archive (100 / month)", "60 requests / min · 2,000 / day", "Included in the TourismIntel PRO plan (web + mobile + MCP)"],
                cta: { label: "Request Beta PRO access →", target: "pro-beta" },
                note: "Web payments are coming soon — Beta access is granted manually while we finish billing.",
              },
            ].map((t) => (
              <div key={t.tier} data-testid={`mcp-tier-${t.tier.toLowerCase()}`} style={{ background: "#fff", border: "1px solid #e8ecee", borderTop: `3px solid ${t.accent}`, borderRadius: 14, padding: 20 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                  <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#191c1d" }}>{t.tier}</p>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: t.accent }}>{t.price}</span>
                </div>
                <ul style={{ margin: "12px 0 0", paddingLeft: 18, fontSize: 13, color: "#3b494c", lineHeight: 1.8 }}>
                  {t.items.map((i) => <li key={i}>{i}</li>)}
                </ul>
                {t.cta && (
                  <button
                    onClick={() => scrollTo(t.cta.target)}
                    data-testid={`mcp-tier-${t.tier.toLowerCase()}-cta`}
                    style={{ display: "inline-block", marginTop: 14, background: t.accent, color: "#fff", border: "none", cursor: "pointer", borderRadius: 999, padding: "9px 20px", fontSize: 13, fontWeight: 700 }}
                  >
                    {t.cta.label}
                  </button>
                )}
                {t.note && (
                  <p style={{ margin: "12px 0 0", fontSize: 11, color: "#6b7a7d", lineHeight: 1.6 }}>{t.note}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beta PRO access */}
      <section id="pro-beta" style={{ background: "#faf5ff", padding: "48px 24px", borderTop: "1px solid #e9d5ff" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Sparkles style={{ width: 18, height: 18, color: "#7c3aed" }} />
            <h2 style={{ ...sectionH2, color: "#3b1a6e" }}>Request Beta PRO access</h2>
          </div>
          <p style={{ margin: "0 0 22px", fontSize: 14, color: "#6b7a7d", lineHeight: 1.6 }}>
            Web payments are launching soon. Meanwhile, we grant PRO access manually to Beta
            testers — tell us who you are and how you&apos;d use <code style={{ background: "#f3e8ff", padding: "2px 6px", borderRadius: 6, fontSize: 12, color: "#7c3aed" }}>ask_tourismintel</code>.
          </p>
          <ProBetaRequestForm dark={false} />
        </div>
      </section>

      {/* Get key form */}
      <section id="get-key" style={{ background: "#0c1011", padding: "56px 24px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <KeyRound style={{ width: 18, height: 18, color: "#22d3ee" }} />
            <h2 style={{ ...sectionH2, color: "#f1f5f9" }}>Get your free key</h2>
          </div>
          <p style={{ margin: "0 0 22px", fontSize: 14, color: "#94a3b8", lineHeight: 1.6 }}>
            Instant, no credit card. One active key per email — we&apos;ll also email you a copy.
          </p>

          {issued ? (
            <div data-testid="mcp-generated-key" style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.3)", borderRadius: 14, padding: 20 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
                <AlertTriangle style={{ width: 16, height: 16, color: "#fbbf24", flexShrink: 0, marginTop: 2 }} />
                <p style={{ margin: 0, fontSize: 13, color: "#fbbf24", fontWeight: 600 }}>
                  Save this key now — it won&apos;t be shown again.
                  {issued.email_sent ? " A copy was sent to your email." : " (Email delivery unavailable — copy it now.)"}
                  {issued.account_created ? " Your free TourismIntel account was created too — welcome!" : ""}
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#0c1011", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px 14px" }}>
                <code style={{ flex: 1, fontSize: 13, color: "#22d3ee", wordBreak: "break-all", fontFamily: "ui-monospace, monospace" }}>{issued.key}</code>
                <button onClick={copyIssuedKey} data-testid="mcp-copy-key-btn" aria-label="Copy key" style={{ background: "none", border: "none", cursor: "pointer", color: keyCopied ? "#34d399" : "#94a3b8", padding: 6 }}>
                  {keyCopied ? <Check style={{ width: 16, height: 16 }} /> : <Copy style={{ width: 16, height: 16 }} />}
                </button>
              </div>
              <p style={{ margin: "14px 0 0", fontSize: 12, color: "#64748b" }}>
                Server URL: <code style={{ color: "#94a3b8" }}>{MCP_URL}</code> · Header: <code style={{ color: "#94a3b8" }}>Authorization: Bearer &lt;key&gt;</code> · Claude.ai connector: <code style={{ color: "#94a3b8" }}>x-api-key: &lt;key&gt;</code>
              </p>
              <button onClick={() => scrollTo("connect")} data-testid="mcp-after-key-connect-btn" style={{ marginTop: 16, background: "#22d3ee", color: "#001f24", border: "none", borderRadius: 999, padding: "10px 22px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Next: connect your client ↓
              </button>
            </div>
          ) : (
            <form onSubmit={requestKey} data-testid="mcp-key-form" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={80}
                placeholder="Your name or project"
                data-testid="mcp-key-name-input"
                style={{ background: "#1a2122", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "13px 16px", fontSize: 14, color: "#e2e8f0", outline: "none" }}
              />
              {user ? (
                <div data-testid="mcp-key-signed-in" style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.25)", borderRadius: 12, padding: "12px 16px", fontSize: 13, color: "#94a3b8" }}>
                  Signed in as <strong style={{ color: "#22d3ee" }}>{user.email}</strong> — your key will be linked to this account.
                </div>
              ) : (
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  placeholder="you@company.com"
                  data-testid="mcp-key-email-input"
                  style={{ background: "#1a2122", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "13px 16px", fontSize: 14, color: "#e2e8f0", outline: "none" }}
                />
              )}
              <label data-testid="mcp-key-terms-label" style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px" }}>
                <input
                  type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} required
                  data-testid="mcp-key-terms-checkbox"
                  style={{ marginTop: 2, accentColor: "#22d3ee", width: 15, height: 15, flexShrink: 0, cursor: "pointer" }}
                />
                <span style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.6 }}>
                  I accept the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#22d3ee" }}>Terms of Use</a> and{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#22d3ee" }}>Privacy Policy</a>, and I understand the
                  MCP Server is a <strong style={{ color: "#cbd5e1" }}>BETA service</strong> provided &quot;as is&quot;, without warranties
                  or availability guarantees.
                </span>
              </label>
              {formError && (
                <p data-testid="mcp-key-form-error" style={{ margin: 0, fontSize: 13, color: "#f87171" }}>{formError}</p>
              )}
              <button
                type="submit" disabled={submitting || !acceptTerms}
                data-testid="mcp-key-submit-btn"
                style={{ background: "#22d3ee", color: "#001f24", border: "none", borderRadius: 12, padding: "13px 20px", fontSize: 14, fontWeight: 700, cursor: submitting ? "wait" : acceptTerms ? "pointer" : "not-allowed", opacity: submitting || !acceptTerms ? 0.6 : 1 }}
              >
                {submitting ? "Issuing key…" : "Get my free MCP key"}
              </button>
              <p style={{ margin: 0, fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>
                {user
                  ? "Max 3 requests per hour per network. One active key per account."
                  : "Requesting a key also creates your free TourismIntel account (if you don't have one yet). Disposable email addresses are rejected. Max 3 requests per hour per network."}
              </p>
            </form>
          )}
          <p style={{ margin: "18px 0 0", fontSize: 13, color: "#64748b" }}>
            Already have a key?{" "}
            <a href="/mcp/manage" data-testid="mcp-manage-link" style={{ color: "#22d3ee", fontWeight: 600 }}>
              Check usage &amp; rotate it →
            </a>
          </p>
        </div>
      </section>

      {/* Connect guides */}
      <section id="connect" style={{ padding: "48px 24px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <Terminal style={{ width: 18, height: 18, color: "#006874" }} />
            <h2 style={sectionH2}>Connect your client</h2>
          </div>
          <p style={{ ...bodyText, marginBottom: 6 }}>
            Server URL: <code style={{ background: "#eef1f2", padding: "2px 8px", borderRadius: 6, fontSize: 13, color: "#006874" }}>{MCP_URL}</code>
            {" "}· Transport: Streamable HTTP · Auth: <code style={{ background: "#eef1f2", padding: "2px 8px", borderRadius: 6, fontSize: 13, color: "#006874" }}>Authorization: Bearer ti_live_…</code>
            {" "}or <code style={{ background: "#eef1f2", padding: "2px 8px", borderRadius: 6, fontSize: 13, color: "#006874" }} data-testid="mcp-auth-x-api-key">X-API-Key: ti_live_…</code>
          </p>

          {/* Claude.ai custom connector — cannot set Authorization, so we accept x-api-key */}
          <div data-testid="mcp-connect-claude-web" style={{ marginTop: 26 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#191c1d" }}>Claude.ai (web &amp; mobile) — custom connector</h3>
            <ol data-testid="mcp-connect-claude-web-steps" style={{ ...bodyText, fontSize: 14, margin: "6px 0 0", paddingLeft: 20, lineHeight: 1.8 }}>
              <li><strong>Settings → Connectors → Add custom connector</strong> (paid plans).</li>
              <li>Remote MCP server URL: <code style={{ fontSize: 12 }}>{MCP_URL}</code></li>
              <li>Open <strong>Advanced settings → Additional request headers</strong> and add one header: name <code style={{ fontSize: 12 }} data-testid="mcp-claude-web-header-name">x-api-key</code>, value = your <code style={{ fontSize: 12 }}>ti_live_…</code> key, keep <strong>Required</strong> checked.</li>
              <li>Save — the TourismIntel tools appear in the chat tool menu.</li>
            </ol>
            <p style={{ ...bodyText, fontSize: 13, margin: "8px 0 0", color: "#6b7a7d" }}>
              Claude reserves the <code style={{ fontSize: 12 }}>authorization</code> header for its own OAuth token (it is greyed out in the list), so the
              connector must send the key as <code style={{ fontSize: 12 }}>x-api-key</code> — our server accepts it with the same tier, quotas and audit as the Bearer form.
            </p>
            <CodeBlock id="claude-web" code={`URL:    ${MCP_URL}\nHeader: x-api-key: YOUR_KEY   (Required ✓)`} />
          </div>

          {/* Claude Desktop */}
          <div data-testid="mcp-connect-claude-desktop" style={{ marginTop: 26 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#191c1d" }}>Claude Desktop</h3>
            <p style={{ ...bodyText, fontSize: 14, margin: "6px 0 0" }}>
              Add this to <code style={{ fontSize: 12 }}>claude_desktop_config.json</code> (Settings → Developer → Edit Config),
              replacing <code style={{ fontSize: 12 }}>YOUR_KEY</code>. Requires Node.js (uses the <code style={{ fontSize: 12 }}>mcp-remote</code> bridge).
            </p>
            <CodeBlock id="claude-desktop" code={`{
  "mcpServers": {
    "tourismintel": {
      "command": "npx",
      "args": [
        "-y", "mcp-remote",
        "${MCP_URL}",
        "--header", "Authorization: Bearer YOUR_KEY"
      ]
    }
  }
}`} />
          </div>

          {/* Cursor */}
          <div data-testid="mcp-connect-cursor" style={{ marginTop: 26 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#191c1d" }}>Cursor</h3>
            <p style={{ ...bodyText, fontSize: 14, margin: "6px 0 0" }}>
              Create <code style={{ fontSize: 12 }}>.cursor/mcp.json</code> in your project (or <code style={{ fontSize: 12 }}>~/.cursor/mcp.json</code> globally):
            </p>
            <CodeBlock id="cursor" code={`{
  "mcpServers": {
    "tourismintel": {
      "url": "${MCP_URL}",
      "headers": { "Authorization": "Bearer YOUR_KEY" }
    }
  }
}`} />
          </div>

          {/* ChatGPT — coming soon via OAuth */}
          <div data-testid="mcp-connect-chatgpt" style={{ marginTop: 26, background: "#fff", border: "1px solid #e8ecee", borderRadius: 14, padding: "18px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#191c1d" }}>ChatGPT</h3>
              <span data-testid="mcp-chatgpt-coming-soon" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c3aed", background: "#f3e8ff", borderRadius: 999, padding: "3px 10px" }}>Coming soon</span>
            </div>
            <p style={{ ...bodyText, fontSize: 14, margin: "8px 0 0" }}>
              ChatGPT&apos;s custom connectors accept only <strong>OAuth</strong> or <strong>no authentication</strong> — there is no field for an API-key header,
              so a <code style={{ fontSize: 12 }}>ti_live_…</code> key cannot be used there today. TourismIntel is shipping <strong>OAuth 2.0 sign-in</strong>:
              ChatGPT users will log in with their TourismIntel account and keep their tier and quotas. Until then, Claude.ai, Claude Desktop and Cursor work fully with your key.
            </p>
            <McpChatGptWaitlist />
          </div>

          {/* curl smoke tests */}
          <div data-testid="mcp-connect-curl" style={{ marginTop: 26 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#191c1d" }}>Quick test (curl)</h3>
            <CodeBlock id="curl" code={`curl -s -X POST ${MCP_URL} \\
  -H "Authorization: Bearer YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`} />
            <p style={{ ...bodyText, fontSize: 14, margin: "14px 0 0" }}>Call a tool with filters — here <code style={{ fontSize: 12 }}>list_events</code> for hospitality trade shows in Europe in November:</p>
            <CodeBlock id="curl-events" code={`curl -s -X POST ${MCP_URL} \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":2,"method":"tools/call","params":{"name":"list_events",
       "arguments":{"region":"europe","month":"2026-11","theme":"hospitality","limit":5}}}'`} />
          </div>

          {/* first prompt in Claude */}
          <div data-testid="mcp-connect-test-prompt" style={{ marginTop: 26 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#191c1d" }}>Test it in Claude</h3>
            <p style={{ ...bodyText, fontSize: 14, margin: "6px 0 0" }}>Once the connector is on, paste this prompt — Claude should call <code style={{ fontSize: 12 }}>get_trending</code> and <code style={{ fontSize: 12 }}>list_events</code> and cite tourismintel.ai links:</p>
            <CodeBlock id="claude-test" code={`Using TourismIntel: what are the 3 travel stories going viral right now, and which industry events take place in Europe next month? Link the sources.`} />
          </div>
        </div>
      </section>

      <McpToolReference catalog={catalog} live={live} />
      <McpResponses catalog={catalog} />
      <McpManageKey catalog={catalog} />
      <McpRoadmap catalog={catalog} />
      <McpFaq />

      {/* Security & terms */}
      <section style={{ padding: "40px 24px 56px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <Shield style={{ width: 18, height: 18, color: "#006874" }} />
            <h2 style={sectionH2}>Security &amp; fair use</h2>
          </div>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: "#3b494c", lineHeight: 1.9 }}>
            <li>Keys are stored as SHA-256 hashes — we can never see or recover your raw key. Keep it secret.</li>
            <li>Tool responses mark third-party content as data, not instructions, mitigating prompt-injection risk.</li>
            <li>Rate limits protect the service for everyone; automated bulk extraction or full-text scraping is not permitted.</li>
            <li>Usage is governed by our <a href="/terms" style={{ color: "#006874", fontWeight: 600 }}>Terms of Use</a>. Keys can be revoked for abuse.</li>
            <li>Questions or PRO upgrades: <a href="mailto:hello@tourismintel.ai" style={{ color: "#006874", fontWeight: 600 }}>hello@tourismintel.ai</a></li>
          </ul>
        </div>
      </section>

      <PublicPageFooter />
    </div>
  );
}
